import { createRouter } from '@tanstack/react-router';
import qs from 'query-string';

// relative imports
import LoadingPage from './components/shared/page-components/LoadingPage';
import NotFoundPage from './components/shared/route-management/NotFoundPage';
import { routeTree } from './routeTree.gen';

// Sets up a Router instance
// NOTE: custom stringifySearch and parseSearch are needed to handle the large number
// issue with QuickBooks companyId in redirect parsing and followon effects due to that
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingMinMs: 0,
  defaultPendingComponent: () => <LoadingPage loadingApplication />,
  defaultNotFoundComponent: NotFoundPage,
  scrollRestoration: true,
  context: {
    auth0: undefined!,
    apolloClient: undefined!,
  },
  stringifySearch: (searchValues) => {
    // Early return for empty values
    if (!searchValues || Object.keys(searchValues).length === 0) {
      return '';
    }

    // Process search values with proper handling for different types
    const processedValues = Object.entries(searchValues).reduce(
      (acc, [key, value]) => {
        if (value === null || value === undefined) {
          return acc;
        }

        if (typeof value === 'object') {
          // For objects, we need to handle them carefully
          if (Array.isArray(value)) {
            // Arrays should be handled by qs.stringify with arrayFormat: 'bracket'
            acc[key] = value;
          } else {
            // Objects get JSON stringified for OAuth compatibility
            acc[key] = JSON.stringify(value);
          }
        } else if (typeof value === 'number') {
          // Numbers (like QuickBooks companyId) are passed through unchanged
          // This prevents the corruption issue with large numbers
          acc[key] = value;
        } else {
          // All other primitive values pass through unchanged
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );

    const searchString = qs.stringify(processedValues, {
      arrayFormat: 'bracket',
    });
    return searchString ? `?${searchString}` : '';
  },
  parseSearch: (searchString) => {
    if (!searchString || searchString === '?' || searchString === '') {
      return {};
    }

    const parsed = qs.parse(searchString, { arrayFormat: 'bracket' });

    // Reconstruct the search parameters with proper type handling
    const reconstructed = Object.entries(parsed).reduce(
      (acc, [key, value]) => {
        if (typeof value === 'string') {
          // Check if this string represents a JSON object
          const trimmed = value.trim();
          if (
            trimmed.length > 2 &&
            ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
              (trimmed.startsWith('[') && trimmed.endsWith(']')))
          ) {
            try {
              const parsedValue = JSON.parse(trimmed);
              if (typeof parsedValue === 'object' && parsedValue !== null) {
                acc[key] = parsedValue;
                return acc;
              }
            } catch (e) {
              // JSON parsing failed, keep as string
              acc[key] = value;
              return acc;
            }
          }
        }

        // For all other values (including numbers, arrays, strings), keep as-is
        // This preserves QuickBooks companyId values and other primitive types
        acc[key] = value;
        return acc;
      },
      {} as Record<string, unknown>,
    );

    return reconstructed;
  },
});

export default router;
